<template>
  <div v-if="!isAuthenticated" class="resource-views-banner mobile-only">
    <span class="banner-text">
      You have
      <span class="remaining-count">{{ limitResourceLeft }}</span
      >/3 resource views remaining.
      <button class="login-signup-btn" @click="openLoginModal">
        Sign In or Sign Up
      </button>
      for free to unlock full access!
    </span>
  </div>
  <div class="instructional-preview-container">
    <!-- Loading Overlay -->
    <LoadingOverlay
      :model-value="isLoading"
      message="Loading Lesson Materials..."
      :full-screen="true"
      :teleport-to-body="true"
    />

    <!-- Error State -->
    <section
      v-if="!isLoading && isForbiddenError"
      class="lesson-materials-forbidden-state"
      role="alert"
      aria-live="polite"
    >
      <div class="lesson-materials-forbidden-content">
        <img
          class="lesson-materials-forbidden-image"
          src="/images/errors/not-yet-blue.svg"
          alt=""
          aria-hidden="true"
        />
        <div class="lesson-materials-forbidden-copy">
          <h1>Failed to Load Lesson Materials</h1>
          <p>
            We couldn’t display the lesson content because you’re not signed in.
            Please sign in to access the full lesson materials.
          </p>
        </div>
        <ActionButton
          variant="primary"
          size="large"
          class="lesson-materials-forbidden-button"
          @click="goToLoginPage"
        >
          <span>Go to Sign In</span>
          <template #icon-right>
            <ChevronRightIcon :color="'white'" />
          </template>
        </ActionButton>
      </div>
    </section>

    <ErrorState
      v-else-if="!isLoading && error"
      title="Failed to Load Lesson Materials"
      :message="error"
      :actions="errorActions"
      @action="handleErrorAction"
    />

    <!-- Main Content -->
    <div
      v-if="!isLoading && !error && instructionalData.title"
      class="instructional-content-wrapper"
    >
      <div class="instructional-main-content">
        <div class="instructional-header">
          <div class="instructional-header-content">
            <div class="instructional-navigation-container">
              <BreadCrumbs
                v-if="shouldShowBreadcrumbs"
                :items="breadcrumbsItems"
              />
            </div>
            <div class="instructional-title-container">
              <h1 class="instructional-title">
                {{ instructionalData.title || "Untitled Educator's Guide" }}
              </h1>
              <div
                v-if="instructionalData.short_description"
                class="instructional-subtitle"
              >
                {{ instructionalData.short_description }}
              </div>
              <div class="instructional-meta">
                <div
                  class="instructional-meta-item"
                  v-if="instructionalData.duration"
                >
                  <div class="instructional-meta-item-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                  </div>
                  <span>{{ instructionalData.duration }} minutes</span>
                </div>
                <div
                  v-if="instructionalData.grade"
                  class="instructional-meta-item"
                >
                  <div class="instructional-meta-item-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                      <path d="M6 12v5c3 3 9 3 12 0v-5" />
                    </svg>
                  </div>
                  <span>{{ instructionalData.grade }}</span>
                </div>
                <div
                  v-if="instructionalData.subject"
                  class="instructional-meta-item"
                >
                  <div class="instructional-meta-item-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path
                        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                      />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>
                  </div>
                  <span style="text-transform: capitalize">{{
                    instructionalData.subject
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="instructional-main-container">
          <div
            class="instructional-content"
            :class="{ expanded: sidebarCollapsed }"
          >
            <div class="instructional-tabs">
              <div
                v-for="(tab, index) in instructionalData.tabs"
                :key="index"
                class="instructional-tab"
                :class="{ active: activeTabIndex === index }"
                @click="selectTab(index)"
              >
                {{ tab.name }}
                <div
                  v-if="tab.duration && activeTabIndex === index"
                  class="tab-duration"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <span>{{ tab.duration }} Minutes</span>
                </div>
              </div>
            </div>

            <div
              class="instructional-tab-content"
              ref="instructionalTabContentRef"
              v-if="instructionalData.tabs && instructionalData.tabs.length > 0"
            >
              <template v-if="currentTab.type === 'assessment'">
                <AssessmentView :assessments="currentTab.assessments" />
              </template>
              <template v-else>
                <div
                  v-for="(section, sectionIndex) in getCurrentTabSections()"
                  :key="sectionIndex"
                  class="instructional-section"
                >
                  <div
                    class="tiptap-content"
                    v-html="addTableHeaders(section.content)"
                  ></div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Resources Section -->
        <div
          v-if="hasResources && activeTabIndex === 0"
          class="resources-section"
        >
          <h2>Resources</h2>
          <div class="resources-buttons">
            <!-- Slide Deck Button -->
            <ActionButton
              v-if="instructionalData.slide_url"
              variant="primary"
              class="resource-copy-button"
              @click="openCopySlideModal"
            >
              <template #icon-left>
                <v-icon start>mdi-content-copy</v-icon>
              </template>
              <span>Copy <strong>Slide Deck</strong> to your Google Drive</span>
            </ActionButton>

            <!-- Instructional Materials -->
            <ActionButton
              v-for="material in instructionalData.educator_guide_instructional"
              :key="material.id || material.name"
              variant="primary"
              class="resource-copy-button"
              @click="openCopyResourceModal(material)"
            >
              <template #icon-left>
                <v-icon start>mdi-content-copy</v-icon>
              </template>
              <span
                >Copy <strong>{{ material.name }}</strong> to your Google
                Drive</span
              >
            </ActionButton>
          </div>
        </div>

        <!-- Resource Views Banner -->
        <div v-if="!isAuthenticated" class="resource-views-banner">
          <span class="banner-text">
            You have
            <span class="remaining-count">{{ limitResourceLeft }}</span
            >/3 resource views remaining.
            <button class="login-signup-btn" @click="openLoginModal">
              Sign In or Sign Up
            </button>
            for free to unlock full access!
          </span>
        </div>
      </div>

      <!-- Right Sidebar (Desktop) -->
      <div
        class="instructional-sidebar desktop-only"
        :class="{ collapsed: sidebarCollapsed }"
      >
        <div class="sidebar-header">
          <button
            class="sidebar-toggle"
            @click="sidebarCollapsed = !sidebarCollapsed"
            :title="sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'"
            :aria-label="
              sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'
            "
            :aria-expanded="!sidebarCollapsed"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline
                :points="
                  sidebarCollapsed ? '9 18 15 12 9 6' : '15 18 9 12 15 6'
                "
              ></polyline>
            </svg>
          </button>
        </div>

        <div class="sidebar-content" v-if="!sidebarCollapsed">
          <!-- Maker Approach Section -->
          <div
            v-if="instructionalData.maker_approach"
            class="sidebar-section maker-approach-section"
          >
            <h3 class="sidebar-section-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 20h9" />
                <path
                  d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"
                />
              </svg>
              Maker Approach
            </h3>
            <a
              href="https://makered.org/wp-content/uploads/2019/09/MakerEd_Resource_Approaches_v6.pdf"
              target="_blank"
              rel="noopener noreferrer"
              class="maker-approach-link"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
                ></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
              What’s this?
            </a>
            <div class="maker-approach-content">
              <v-tooltip
                location="bottom"
                :text="makerApproach?.description"
                :disabled="!makerApproach"
                content-class="custom-tooltip"
                transition="fade-transition"
                open-delay="200"
                close-delay="100"
                max-width="400"
              >
                <template v-slot:activator="{ props }">
                  <p class="maker-approach-text tooltip-trigger" v-bind="props">
                    {{
                      makerApproach?.title || instructionalData.maker_approach
                    }}
                    <svg
                      v-if="makerApproach"
                      xmlns="http://www.w3.org/2000/svg"
                      width="30"
                      height="30"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="info-icon"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4" />
                      <path d="M12 8h.01" />
                    </svg>
                  </p>
                </template>
              </v-tooltip>
            </div>
          </div>

          <!-- Materials Provided Section -->
          <div
            v-if="
              instructionalData.material_provided &&
              instructionalData.material_provided.length > 0
            "
            class="sidebar-section materials-section"
          >
            <h3 class="sidebar-section-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"
                />
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
              </svg>
              Materials
              <span class="materials-count"
                >({{ instructionalData.material_provided.length }})</span
              >
            </h3>
            <div class="materials-list">
              <div
                v-for="material in instructionalData.material_provided"
                :key="material.id"
                class="material-item"
              >
                <div class="material-header">
                  <h4 class="material-name">{{ material.name }}</h4>
                  <span v-if="material.includedInMC" class="included-badge">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {{ includedBadgeLabel }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Standards Section -->
          <div
            v-if="standardCategories.length > 0"
            class="sidebar-section standards-section"
          >
            <h3 class="sidebar-section-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                <path d="M6 12v5c3 3 9 3 12 0v-5" />
              </svg>
              Standards
            </h3>
            <div class="standards-list">
              <div
                v-for="(category, index) in standardCategories"
                :key="index"
                class="standard-category"
              >
                <div class="standard-category-title">{{ category.title }}</div>
                <div class="standard-terms">
                  <template v-if="category.terms && category.terms.length > 0">
                    <template
                      v-for="(term, termIndex) in category.terms"
                      :key="termIndex"
                    >
                      <template v-if="term.description">
                        <v-tooltip
                          :text="term.description"
                          location="top"
                          max-width="300"
                          content-class="custom-tooltip"
                        >
                          <template v-slot:activator="{ props }">
                            <button class="standard-tag" v-bind="props">
                              {{ term.title }}
                            </button>
                          </template>
                        </v-tooltip>
                      </template>
                      <template v-else>
                        <button class="standard-tag">{{ term.title }}</button>
                      </template>
                    </template>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Instructional Info Section -->
          <div class="sidebar-section instructional-info-section">
            <h3 class="sidebar-section-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              Guide Details
            </h3>
            <div class="instructional-details">
              <div class="detail-item" v-if="instructionalData.grade">
                <span class="detail-label">Grade Level:</span>
                <span class="detail-value">{{ instructionalData.grade }}</span>
              </div>
              <div class="detail-item" v-if="instructionalData.subject">
                <span class="detail-label">Subject:</span>
                <span class="detail-value">{{
                  instructionalData.subject
                }}</span>
              </div>
              <div class="detail-item" v-if="instructionalData.focus">
                <span class="detail-label">Focus:</span>
                <span class="detail-value" style="text-transform: capitalize">{{
                  instructionalData.focus
                }}</span>
              </div>
              <div class="detail-item" v-if="instructionalData.duration">
                <span class="detail-label">Duration:</span>
                <span class="detail-value"
                  >{{ instructionalData.duration }} minutes</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Bottom Sheet Button -->
    <div class="mobile-details-button-container">
      <ActionButton
        variant="outline"
        class="mobile-details-button"
        @click="showBottomSheet = true"
        v-if="!isLoading && !error && instructionalData.title"
      >
        View More Details
      </ActionButton>
    </div>

    <!-- Mobile Bottom Sheet -->
    <v-bottom-sheet v-model="showBottomSheet" class="mobile-bottom-sheet">
      <v-card class="bottom-sheet-card">
        <div class="bottom-sheet-header">
          <h3>Guide Details</h3>
          <button class="bottom-sheet-close" @click="showBottomSheet = false">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div class="sidebar-content mobile-sidebar-content">
          <!-- Maker Approach Section -->
          <div
            v-if="instructionalData.maker_approach"
            class="sidebar-section maker-approach-section"
          >
            <h3 class="sidebar-section-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 20h9" />
                <path
                  d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"
                />
              </svg>
              Maker Approach
            </h3>
            <a
              href="https://makered.org/wp-content/uploads/2019/09/MakerEd_Resource_Approaches_v6.pdf"
              target="_blank"
              rel="noopener noreferrer"
              class="maker-approach-link"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
                ></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
              What’s this?
            </a>
            <div class="maker-approach-content">
              <v-tooltip
                location="bottom"
                :text="makerApproach?.description"
                :disabled="!makerApproach"
                content-class="custom-tooltip"
                transition="fade-transition"
                open-delay="200"
                close-delay="100"
                max-width="400"
              >
                <template v-slot:activator="{ props }">
                  <p class="maker-approach-text tooltip-trigger" v-bind="props">
                    {{
                      makerApproach?.title || instructionalData.maker_approach
                    }}
                    <svg
                      v-if="makerApproach"
                      xmlns="http://www.w3.org/2000/svg"
                      width="30"
                      height="30"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="info-icon"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4" />
                      <path d="M12 8h.01" />
                    </svg>
                  </p>
                </template>
              </v-tooltip>
            </div>
          </div>

          <!-- Materials Provided Section -->
          <div
            v-if="
              instructionalData.material_provided &&
              instructionalData.material_provided.length > 0
            "
            class="sidebar-section materials-section"
          >
            <h3 class="sidebar-section-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"
                />
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
              </svg>
              Materials
              <span class="materials-count"
                >({{ instructionalData.material_provided.length }})</span
              >
            </h3>
            <div class="materials-list">
              <div
                v-for="material in instructionalData.material_provided"
                :key="material.id"
                class="material-item"
              >
                <div class="material-header">
                  <h4 class="material-name">{{ material.name }}</h4>
                  <span v-if="material.includedInMC" class="included-badge">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    MakerCart
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Standards Section -->
          <div
            v-if="standardCategories.length > 0"
            class="sidebar-section standards-section"
          >
            <h3 class="sidebar-section-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                <path d="M6 12v5c3 3 9 3 12 0v-5" />
              </svg>
              Standards
            </h3>
            <div class="standards-list">
              <div
                v-for="(category, index) in standardCategories"
                :key="index"
                class="standard-category"
              >
                <div class="standard-category-title">{{ category.title }}</div>
                <div class="standard-terms">
                  <template v-if="category.terms && category.terms.length > 0">
                    <template
                      v-for="(term, termIndex) in category.terms"
                      :key="termIndex"
                    >
                      <template v-if="term.description">
                        <v-tooltip
                          :text="term.description"
                          location="top"
                          max-width="300"
                          content-class="custom-tooltip"
                        >
                          <template v-slot:activator="{ props }">
                            <button class="standard-tag" v-bind="props">
                              {{ term.title }}
                            </button>
                          </template>
                        </v-tooltip>
                      </template>
                      <template v-else>
                        <button class="standard-tag">{{ term.title }}</button>
                      </template>
                    </template>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Instructional Info Section -->
          <div class="sidebar-section instructional-info-section">
            <h3 class="sidebar-section-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              Guide Details
            </h3>
            <div class="instructional-details">
              <div class="detail-item" v-if="instructionalData.grade">
                <span class="detail-label">Grade Level:</span>
                <span class="detail-value">{{ instructionalData.grade }}</span>
              </div>
              <div class="detail-item" v-if="instructionalData.subject">
                <span class="detail-label">Subject:</span>
                <span class="detail-value">{{
                  instructionalData.subject
                }}</span>
              </div>
              <div class="detail-item" v-if="instructionalData.focus">
                <span class="detail-label">Focus:</span>
                <span class="detail-value" style="text-transform: capitalize">{{
                  instructionalData.focus
                }}</span>
              </div>
              <div class="detail-item" v-if="instructionalData.duration">
                <span class="detail-label">Duration:</span>
                <span class="detail-value"
                  >{{ instructionalData.duration }} minutes</span
                >
              </div>
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Copy Slide Deck Modal -->
    <CopySlideDeck
      :is-open="isCopySlideModalOpen"
      :file-url="currentCopyResource.url"
      :file-name="currentCopyResource.name"
      @update:is-open="isCopySlideModalOpen = $event"
    />

    <!-- Login Required Modal -->
    <LoginRequiredModal
      v-model="showLoginRequiredModal"
      title="Login to Copy Resources"
      description="Please Sign In or Sign Up to copy resources to your Google Drive and access other premium features."
      :redirect-path="$route.path"
      @close="showLoginRequiredModal = false"
    />

    <!-- Login Modal -->
    <AuthModal
      :is-open="showLoginModal"
      @close="showLoginModal = false"
      @login-success="handleLoginSuccess"
      @goto-signup="handleGotoSignup"
      @email-verified="handleLoginSuccess"
      :z-index="99999"
    />

    <ChatbotAssistant
      position="right"
      :button-bottom-offset="50"
      page-name="Lesson Materials"
    />
  </div>
</template>

<script setup>
import ErrorState from "@/components/common/ErrorState.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import BreadCrumbs from "@/components/common/BreadCrumbs.vue";
import AssessmentView from "@/components/lesson-library/AssessmentView.vue";
import CopySlideDeck from "@/components/lesson-library/CopySlideDeck.vue";
import AuthModal from "@/components/modal/AuthModal.vue";
import LoginRequiredModal from "@/components/modal/LoginRequiredModal.vue";
import { useUserState } from "@/composeables/useUserState";
import educatorsGuideService from "@/services/educatorsGuideService";
import makercartActivityService from "@/services/makercartActivityService";
import { setupSlideThumbnailHandlers } from "@/tiptap-extensions/slide-deck/slideModalHelper";
import { CATEGORY_TYPES, MAKER_APPROACH_DETAIL } from "@/utils/constants.js";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { setDocumentTitle } from "@/utils/pageTitle";
import ChatbotAssistant from "@/components/maker-cart/ChatbotAssistant.vue";
import ActionButton from "@/components/common/ActionButton.vue";
import { addTableHeaders } from "@/utils/addTableHeaders";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";

const router = useRouter();
const route = useRoute();

const activeTabIndex = ref(0);
const sidebarCollapsed = ref(false);
const isAuthenticated = ref(false);
const instructionalTabContentRef = ref(null);
const isCopySlideModalOpen = ref(false);
const currentCopyResource = ref({ url: "", name: "" });
const isLoading = ref(true);
const error = ref("");
const isForbiddenError = ref(false);
const showLoginRequiredModal = ref(false);
const showLoginModal = ref(false);
const limitResourceLeft = ref(3);
const showBottomSheet = ref(false);

const { isAuthenticated: isUserAuthenticated } = useUserState();

// Pages opened from the Makercrate catalog pass ?source=makercrate
const includedBadgeLabel = computed(() =>
  route.query.source === "makercrate" ? "Makercrate" : "Makercart"
);

// Error state configuration
const errorActions = computed(() => [
  {
    label: "Back to MakerCart",
    variant: "primary",
    type: "go_back",
    handler: goBack,
  },
]);

// Default instructional data structure
const defaultInstructionalShell = {
  id: null,
  title: null,
  short_description: null,
  duration: null,
  grade: null,
  subject: null,
  focus: null,
  maker_approach: null,
  slide_url: "",
  educator_guide_instructional: [],
  material_provided: [],
  categories: [],
  tabs: [],
  status: "",
  public_visibility: false,
};

const instructionalData = ref(
  JSON.parse(JSON.stringify(defaultInstructionalShell))
);

// Move function declarations to the top level
function initAccordions(container) {
  if (!container) return;
  const tooltipBlocks = container.querySelectorAll(".tooltip-block");
  tooltipBlocks.forEach((block) => {
    block.classList.remove("is-open");
    block.removeAttribute("data-open");
    const titleLine = block.querySelector(".tooltip-title-line");
    const bodyWrapper = block.querySelector(".tooltip-body-wrapper");
    const arrowEl = block.querySelector(".accordion-arrow");
    if (titleLine && bodyWrapper) {
      const titleEl = block.querySelector(".tooltip-title");
      const bodyEl = block.querySelector(".tooltip-body");
      if (titleEl) titleEl.setAttribute("contenteditable", "false");
      if (bodyEl) bodyEl.setAttribute("contenteditable", "false");

      const existingHandler = titleLine.__accordionClickHandler__;
      if (existingHandler) {
        titleLine.removeEventListener("click", existingHandler);
      }

      const toggleAccordion = () => {
        const isOpen = block.classList.contains("is-open");
        if (isOpen) {
          block.classList.remove("is-open");
          bodyWrapper.classList.remove("is-open");
          if (arrowEl) arrowEl.classList.remove("is-open");
          block.removeAttribute("data-open");
        } else {
          block.classList.add("is-open");
          bodyWrapper.classList.add("is-open");
          if (arrowEl) arrowEl.classList.add("is-open");
          block.setAttribute("data-open", "");
        }
      };
      titleLine.addEventListener("click", toggleAccordion);
      titleLine.__accordionClickHandler__ = toggleAccordion;
    }
  });
}

let domObserver = null;

const runDomObserver = () => {
  const contentArea = instructionalTabContentRef.value;
  if (contentArea) {
    initAccordions(contentArea);
    setupSlideThumbnailHandlers(contentArea);

    if (typeof MutationObserver !== "undefined") {
      domObserver = new MutationObserver((mutationsList) => {
        let needsAccordionInit = false;
        let needsSlideHandlerInit = false;

        for (const mutation of mutationsList) {
          if (mutation.type === "childList") {
            for (const node of Array.from(mutation.addedNodes).concat(
              Array.from(mutation.removedNodes)
            )) {
              if (node.nodeType === Node.ELEMENT_NODE) {
                if (
                  node.matches(".tooltip-block") ||
                  node.querySelector(".tooltip-block")
                ) {
                  needsAccordionInit = true;
                }
                if (
                  node.matches(".slide-deck-container") ||
                  node.querySelector(".slide-deck-container")
                ) {
                  needsSlideHandlerInit = true;
                }
              }
            }
          }
        }

        if (needsAccordionInit || needsSlideHandlerInit) {
          nextTick(() => {
            if (needsAccordionInit && instructionalTabContentRef.value)
              initAccordions(instructionalTabContentRef.value);
            if (needsSlideHandlerInit && instructionalTabContentRef.value)
              setupSlideThumbnailHandlers(instructionalTabContentRef.value);
          });
        }
      });

      domObserver.observe(contentArea, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }
  }
};

const currentTab = computed(() => {
  if (
    !instructionalData.value.tabs ||
    activeTabIndex.value >= instructionalData.value.tabs.length
  ) {
    return { type: null, assessments: [], sections: [] };
  }
  return instructionalData.value.tabs[activeTabIndex.value];
});

const getCurrentTabSections = () => {
  if (
    instructionalData.value.tabs &&
    instructionalData.value.tabs.length > 0 &&
    activeTabIndex.value < instructionalData.value.tabs.length
  ) {
    return instructionalData.value.tabs[activeTabIndex.value].sections || [];
  }
  return [];
};

const hasResources = computed(() => {
  return (
    instructionalData.value.slide_url ||
    (instructionalData.value.educator_guide_instructional &&
      instructionalData.value.educator_guide_instructional.length > 0)
  );
});

const makerApproach = computed(() => {
  const approach = instructionalData.value.maker_approach?.toLowerCase();
  return MAKER_APPROACH_DETAIL[approach] || "";
});

const shouldShowBreadcrumbs = computed(() => {
  return route.name === "MyMakerCartActivitiesPage";
});

const breadcrumbsItems = computed(() => [
  {
    label: "My Resources",
    to: { name: "MyResourcesPage" },
    active: false,
  },
  {
    label: "Makercart Activities",
    to: {
      name: "MyResourcesPage",
      query: {
        tab: "makercart-activites",
      },
    },
    active: false,
  },
  {
    label: instructionalData.value.title || "Loading...",
    active: true,
  },
]);

// Filter and group standard categories for sidebar display
const standardCategories = computed(() => {
  // First filter standard categories
  const filteredCategories = instructionalData.value.categories.filter(
    (category) => {
      const isMakerCartCategory = category.category_for.includes(
        CATEGORY_TYPES.LessonMaterials
      );
      const isStandardCategory =
        category.name && category.name.includes("standard");
      return isMakerCartCategory && isStandardCategory;
    }
  );

  // Group categories by name and merge terms
  const groupedCategories = {};

  filteredCategories.forEach((category) => {
    const categoryName = category.name;

    if (!groupedCategories[categoryName]) {
      // Create new grouped category
      groupedCategories[categoryName] = {
        id: category.id,
        name: category.name,
        title: category.title,
        category_for: category.category_for,
        terms: [...(category.terms || [])],
      };
    } else {
      // Merge terms from categories with the same name
      const existingTermIds = new Set(
        groupedCategories[categoryName].terms.map((term) => term.id)
      );

      // Add new terms that don't already exist
      const newTerms = (category.terms || []).filter(
        (term) => !existingTermIds.has(term.id)
      );

      groupedCategories[categoryName].terms.push(...newTerms);
    }
  });

  // Convert back to array
  return Object.values(groupedCategories);
});

// Function to handle tab selection with URL synchronization
const selectTab = (index) => {
  activeTabIndex.value = index;
  updateUrlWithTab(index);
};

// Function to update URL with tab parameter
const updateUrlWithTab = (tabIndex) => {
  if (instructionalData.value.tabs && instructionalData.value.tabs[tabIndex]) {
    const tabName = instructionalData.value.tabs[tabIndex].name;
    // Convert tab name to URL-friendly format (lowercase, replace spaces with hyphens)
    const tabSlug = tabName
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

    router.push({
      query: {
        ...route.query,
        tab: tabSlug,
      },
    });
  } else {
    // Remove tab query parameter if invalid index
    const { ...otherQuery } = route.query;
    router.push({ query: otherQuery });
  }
};

// Function to initialize tab from URL query parameter
const initializeTabFromUrl = () => {
  const tabQuery = route.query.tab;
  if (
    tabQuery &&
    instructionalData.value.tabs &&
    instructionalData.value.tabs.length > 0
  ) {
    // Find tab index by matching the slug
    const tabIndex = instructionalData.value.tabs.findIndex((tab) => {
      const tabSlug = tab.name
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
      return tabSlug === tabQuery;
    });

    if (tabIndex !== -1) {
      activeTabIndex.value = tabIndex;
    } else {
      // If tab not found, default to first tab and clean URL
      activeTabIndex.value = 0;
      updateUrlWithTab(0);
    }
  } else {
    // If no tab query or no tabs, default to first tab
    activeTabIndex.value = 0;
  }
};

const openCopySlideModal = () => {
  // Check if user is authenticated first
  if (!isAuthenticated.value) {
    showLoginRequiredModal.value = true;
    return;
  }

  currentCopyResource.value = {
    url: instructionalData.value.slide_url,
    name: "Slide Deck",
  };
  isCopySlideModalOpen.value = true;
};

const openCopyResourceModal = (material) => {
  // Check if user is authenticated first
  if (!isAuthenticated.value) {
    showLoginRequiredModal.value = true;
    return;
  }

  currentCopyResource.value = {
    url: material.url,
    name: material.name,
  };
  isCopySlideModalOpen.value = true;
};

const openLoginModal = () => {
  showLoginModal.value = true;
};

const goToLoginPage = () => {
  router.push({
    name: "LoginPage",
    query: {
      redirect: route.fullPath,
    },
  });
};

const handleLoginSuccess = () => {
  showLoginModal.value = false;
  isAuthenticated.value = true;
  loadInstructionalData();
};

const handleGotoSignup = () => {
  showLoginModal.value = false;
  // Navigate to signup page or open signup modal
  router.push({ name: "RegisterPage" });
};

const loadInstructionalData = async () => {
  const id = route.params.id;

  if (!id) {
    error.value = "No educator's guide slug provided";
    isLoading.value = false;
    return;
  }

  try {
    isLoading.value = true;
    error.value = "";
    isForbiddenError.value = false;

    // Get the limit counter and update the UI for non-authenticated users
    if (!isAuthenticated.value) {
      const limitResponse = await makercartActivityService.getLimitCounter();
      if (limitResponse.status === 200) {
        limitResourceLeft.value = 3 - limitResponse.data.data.count;
      }
    }

    const response =
      route.name === "MyMakerCartActivitiesPage"
        ? await educatorsGuideService.getUserEducatorsGuideById(id)
        : await educatorsGuideService.getEducatorsGuideById(
            id,
            route.query.locale
          );

    if (response.data && response.data.data) {
      const apiData = response.data.data;

      // Map API data to component data
      instructionalData.value.id = apiData.id;
      instructionalData.value.title = apiData.title;
      instructionalData.value.short_description = apiData.short_description;
      instructionalData.value.duration = apiData.duration;
      instructionalData.value.grade = apiData.grade;
      instructionalData.value.subject = apiData.subject;
      instructionalData.value.focus = apiData.focus;
      instructionalData.value.maker_approach = apiData.maker_approach;
      instructionalData.value.slide_url = apiData.slide_url;
      instructionalData.value.educator_guide_instructional =
        apiData.educator_guide_instructional || [];
      instructionalData.value.material_provided =
        apiData.material_provided || [];
      instructionalData.value.categories = apiData.categories || [];
      instructionalData.value.status = apiData.status;
      instructionalData.value.public_visibility = apiData.public_visibility;

      // Set the document title to the lesson materials title
      if (instructionalData.value.title) {
        setDocumentTitle(instructionalData.value.title);
      }

      // Parse and set instructional content
      const contents = JSON.parse(apiData.content || "[]");
      if (contents && contents.length > 0) {
        instructionalData.value.tabs = contents;
      }

      // Initialize tab from URL after tabs are loaded
      nextTick(() => {
        initializeTabFromUrl();

        // Initialize DOM observer after content is loaded
        if (instructionalTabContentRef.value) {
          runDomObserver();
        }
      });
    } else {
      setDocumentTitle("Lesson Materials");
      throw new Error("Invalid response format");
    }
  } catch (err) {
    console.error("Error loading educator's guide:", err);
    if (err.response?.status === 403) {
      setDocumentTitle("Lesson Materials");
      isForbiddenError.value = true;
      error.value =
        "We couldn’t display the lesson content because you’re not signed in. Please sign in to access the full lesson materials.";
    } else {
      error.value =
        err.response?.data?.message ||
        "Failed to load educator's guide. Please try again.";
    }
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  // Set a sensible fallback while loading
  setDocumentTitle("Lesson Materials");

  // Check authentication
  isAuthenticated.value = await isUserAuthenticated();

  loadInstructionalData();
});

onBeforeUnmount(() => {
  if (domObserver) {
    domObserver.disconnect();
  }
  if (instructionalTabContentRef.value) {
    const tooltipBlocks = instructionalTabContentRef.value.querySelectorAll(
      ".tooltip-title-line"
    );
    tooltipBlocks.forEach((titleLine) => {
      if (titleLine.__accordionClickHandler__) {
        titleLine.removeEventListener(
          "click",
          titleLine.__accordionClickHandler__
        );
        delete titleLine.__accordionClickHandler__;
      }
    });
  }
});

const goBack = () => {
  router.push({ name: "MakerCartPage" });
};

const handleErrorAction = (actionData) => {
  // Actions are already handled by their individual handlers
  // This could be used for additional tracking or logging
  console.log("Error action triggered:", actionData.type);
};

watch(activeTabIndex, () => {
  nextTick(() => {
    if (instructionalTabContentRef.value) {
      initAccordions(instructionalTabContentRef.value);
      setupSlideThumbnailHandlers(instructionalTabContentRef.value);
    }
  });
});

// Watch for route query changes (tab parameter)
watch(
  () => route.query.tab,
  (newTab, oldTab) => {
    if (
      newTab !== oldTab &&
      instructionalData.value.tabs &&
      instructionalData.value.tabs.length > 0
    ) {
      initializeTabFromUrl();
    }
  }
);

// Watch for route changes (different educator's guide)
watch(
  () => route.params.id,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      activeTabIndex.value = 0;
      // Clear tab query parameter when navigating to different guide
      const { tab, ...otherQuery } = route.query;
      if (tab) {
        router.replace({ query: otherQuery });
      }
      loadInstructionalData();
    }
  }
);

// Keep document title in sync with lesson materials title
watch(
  () => instructionalData.value.title,
  (newTitle) => {
    if (newTitle && typeof newTitle === "string" && newTitle.trim().length) {
      setDocumentTitle(newTitle);
    }
  }
);
</script>

<style scoped>
.instructional-preview-container {
  margin: 0 auto;
  padding: 35px 0 35px 35px;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 40px);
}

.lesson-materials-forbidden-state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: calc(100vh - 110px);
  padding: 12px 24px;
}

.lesson-materials-forbidden-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  max-width: 620px;
  width: 100%;
  text-align: center;
  color: var(--black-body-text);
}

.lesson-materials-forbidden-image {
  width: 500px;
  max-width: 100%;
  aspect-ratio: 1;
  object-fit: contain;
}

.lesson-materials-forbidden-copy {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.lesson-materials-forbidden-copy h1 {
  margin: 0;
  color: var(--black-body-text);
  font-family: var(--font-family-primary);
  font-size: var(--font-size-headline-large);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-headline-large);
}

.lesson-materials-forbidden-copy p {
  margin: 0;
  color: var(--black-body-text);
  font-family: var(--font-family-primary);
  font-size: var(--font-size-title-big);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-title-big);
  letter-spacing: 0.03px;
}

.lesson-materials-forbidden-button {
  height: 56px;
  padding: 10px 16px;
}

.lesson-materials-forbidden-button :deep(.action-button__text) {
  line-height: var(--line-height-title-big);
  letter-spacing: 0.03px;
}

.lesson-materials-forbidden-button :deep(.v-icon) {
  width: 24px;
  height: 24px;
  font-size: 24px;
}

.instructional-content-wrapper {
  display: flex;
  gap: 30px;
}

.instructional-main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
}

.instructional-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 20px;
  margin-bottom: 20px;
}

.instructional-header-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
}

.instructional-navigation-container {
  display: flex;
  align-items: center;
}

.instructional-title-container {
  width: 100%;
}

.instructional-title {
  font-size: 2.5em;
  margin: 0 0 10px 0;
  color: black;
}

.instructional-subtitle {
  font-size: 1.2em;
  color: #666;
  margin-bottom: 20px;
}

.instructional-meta {
  display: flex;
  gap: 20px;
  color: #777;
  align-items: center;
  margin-top: 20px;
  flex-wrap: wrap;
}

.instructional-meta-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--primary-blue);
  font-size: var(--fs-small);
}

.instructional-meta-item-icon {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.instructional-main-container {
  display: flex;
  position: relative;
  flex: 1;
}

.instructional-content {
  flex: 1;
  transition: all 0.3s ease;
  border-radius: 8px;
  background-color: #fff;
  margin-right: 20px;
}

.instructional-content.expanded {
  margin-right: 0;
}

.instructional-tabs {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  padding-bottom: 40px;
  border-bottom: 1px solid #e0e0e0;
  overflow-x: auto;
  font-size: var(--fs-big);
}

.instructional-tab {
  padding: 8px 20px;
  cursor: pointer;
  color: var(--black-body-text);
  position: relative;
  transition: all 0.2s ease;
  white-space: nowrap;
  border-bottom: 3px solid transparent;
}

.instructional-tab:hover {
  color: var(--primary-pink, #ff5a5f);
  border-bottom-color: var(--primary-pink, #ff5a5f);
}

.instructional-tab.active {
  border-bottom: 3px solid var(--primary-pink, #ff5a5f);
  color: var(--primary-pink, #ff5a5f);
  font-weight: bold;
}

.tab-duration {
  font-size: 0.8em;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -30px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.instructional-tab-content {
  min-height: 300px;
}

.instructional-section {
  margin-bottom: 30px;
}

/* Content styles are now handled globally in tiptap-editor.css */
.tiptap-content table {
  display: table !important;
  width: 100% !important;
  max-width: 100% !important;
  border-collapse: separate !important;
  border-spacing: 0 !important;
  border-radius: 16px !important;
  overflow: hidden !important;
  margin-bottom: 1rem !important;
}

.tiptap-content table tr:first-child td,
.tiptap-content table tr:first-child th {
  background-color: var(--primary-blue) !important;
  color: var(--white) !important;
  font-size: var(--fs-large) !important;
  font-weight: 500 !important;
  line-height: 28px !important;
  padding: 20px 10px !important;
  border: none !important;
  border-bottom: 1px solid var(--neutral-300) !important;
  border-top: 1px solid var(--neutral-300) !important;
}

.tiptap-content table td,
.tiptap-content table th {
  min-width: 120px !important;
  white-space: normal !important;
  padding: 20px 10px !important;
  border: none !important;
  border-bottom: 1px solid var(--neutral-300) !important;
  font-size: var(--fs-medium) !important;
  font-weight: 400 !important;
  line-height: 24px !important;
  color: var(--black-body-text) !important;
  vertical-align: top !important;
  background-color: transparent !important;
}

.tiptap-content table td:first-child,
.tiptap-content table th:first-child {
  border-left: 1px solid var(--neutral-300) !important;
}

.tiptap-content table td:last-child,
.tiptap-content table th:last-child {
  border-right: 1px solid var(--neutral-300) !important;
}

@media (max-width: 768px) {
  .tiptap-content table {
    display: block !important;
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important;
  }
}

.tiptap-content h2 {
  margin-top: 0;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.resources-section h2 {
  margin-bottom: 20px;
  color: #333;
  font-size: 1.5em;
}

.resources-buttons {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.resource-copy-button {
  border-radius: 15px;
  text-transform: none;
  font-size: var(--fs-big);
  width: fit-content;
}

/* Sidebar Styles */
.instructional-sidebar {
  width: 360px;
  min-width: 360px;
  background-color: #fff;
  border: 1px solid var(--neutral-300, #bac1cc);
  border-radius: 12px;
  height: fit-content;
  position: sticky;
  top: 20px;
  padding: 12px;
  transition: all 0.3s ease;
  flex-direction: column;
}

.instructional-sidebar.desktop-only {
  display: flex;
}

.instructional-sidebar.collapsed {
  width: 64px;
  min-width: 64px;
}

.sidebar-header {
  padding: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex: 0 0 auto;
  border-radius: 8px;
}

.sidebar-toggle {
  background: none;
  border: none;
  cursor: pointer;
  width: 40px;
  height: 40px;
  padding: 0;
  border-radius: 8px;
  color: var(--black-body-text, #3b4648);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 40px;
}

.sidebar-toggle:hover {
  background-color: var(--white-hover-1, #f2f3fb);
}

.sidebar-content {
  padding: 24px 0 0;
}

.sidebar-section {
  border-bottom: 1px solid #f0f0f0;
  padding: 20px;
}

.sidebar-section:last-child {
  border-bottom: none;
}

.sidebar-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: var(--fs-large);
  font-weight: 500;
  color: var(--primary-blue);
  margin: 0 0 15px 0;
}

.sidebar-section-title svg {
  color: var(--primary-blue, #3498db);
}

.maker-approach-section .sidebar-section-title {
  margin-bottom: 24px;
}

.maker-approach-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-family-primary);
  font-size: var(--fs-small, 14px);
  font-weight: var(--font-weight-bold, 700);
  line-height: var(--line-height-body-medium, 20px);
  letter-spacing: 0.014px;
  color: var(--primary-pink, #e00069);
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease;
  white-space: nowrap;
  padding: 10px 16px;
  border: 1px solid var(--primary-pink, #e00069);
  border-radius: 8px;
  margin: 0 0 24px 0;
  width: fit-content;
  height: 40px;
}

.maker-approach-link svg {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: var(--primary-pink, #e00069);
}

.maker-approach-link:hover {
  opacity: 0.8;
  color: var(--primary-pink, #e00069);
}

.materials-count {
  font-size: 0.8em;
  color: #666;
  font-weight: 400;
  margin-left: 5px;
}

.materials-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.material-item {
  background-color: var(--white-hover-1);
  border-radius: 8px;
  padding: 12px;
  border-left: 4px solid var(--primary-blue, #3498db);
  transition: all 0.2s ease;
}

.material-item:hover {
  background-color: #e9eaf5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.material-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
  gap: 10px;
}

.material-name {
  margin: 0;
  font-size: var(--fs-big);
  font-weight: 600;
  color: #333;
  line-height: 1.3;
  flex: 1;
}

.included-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: var(--green-dark-1);
  color: white;
  padding: 3px 6px;
  border-radius: 12px;
  font-size: 0.7em;
  font-weight: 500;
  white-space: nowrap;
}

.maker-approach-content {
  background: var(--white-hover-1, #f2f3fb);
  border-radius: 8px;
  padding: 12px;
  border-left: 6px solid var(--primary-blue, #003d6a);
}

.maker-approach-text {
  margin: 0;
  font-size: var(--fs-big, 20px);
  font-weight: 500;
  color: var(--black-body-text, #3b4648);
  line-height: 28px;
}

.maker-approach-text.tooltip-trigger {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  transition: all 0.2s ease;
  padding: 0;
  border-radius: 4px;
}

.maker-approach-text.tooltip-trigger:hover {
  color: var(--black-body-text);
  opacity: 0.9;
}

.info-icon {
  color: var(--primary-blue, #003d6a);
  opacity: 1;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.maker-approach-text.tooltip-trigger:hover .info-icon {
  transform: scale(1.1);
}

.instructional-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: var(--white-hover);
  border-radius: 6px;
  border: 1px solid var(--white-hover);
  transition: all 0.2s ease;
  font-size: var(--fs-big);
}

.detail-item:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.detail-label {
  font-weight: 500;
  color: #555;
  font-size: 0.9em;
}

.detail-value {
  font-weight: 600;
  color: #333;
  font-size: 0.9em;
  text-align: right;
}

.detail-value.status {
  padding: 3px 8px;
  border-radius: 12px;
  color: white;
  font-size: 0.8em;
  text-transform: capitalize;
}

.detail-value.status.published {
  background-color: #28a745;
}

.detail-value.status.draft {
  background-color: #6c757d;
}

.detail-value.status.archived {
  background-color: #dc3545;
}

.standards-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.standard-category {
  background: #ffffff;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #e8e8e8;
  transition: all 0.2s ease;
}

.standard-category:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.standard-category-title {
  font-weight: 600;
  color: var(--black-body-text);
  margin-bottom: 8px;
  font-size: var(--fs-big);
  text-transform: capitalize;
}

.standard-terms {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.standard-tag {
  background-color: var(--white-hover);
  border: 1px solid var(--white-hover);
  border-radius: 6px;
  font-size: var(--fs-big);
  padding: 6px 12px;
  color: var(--black-body-text);
  display: inline-block;
  transition: all 0.2s ease;
  cursor: default;
}

.standard-tag:hover {
  background-color: #e5e7eb;
  border-color: #d1d5db;
}

.empty-standards-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 15px 10px;
  text-align: center;
  background-color: #f8fafc;
  border: 1px dashed #e2e8f0;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.empty-standards-state:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
}

.empty-standards-icon {
  margin-bottom: 6px;
  opacity: 0.6;
  transition: all 0.3s ease;
}

.empty-standards-state:hover .empty-standards-icon {
  opacity: 0.8;
  transform: translateY(-1px);
}

.empty-standards-text {
  color: #94a3b8;
  font-size: 0.75rem;
  margin: 0;
  font-style: italic;
}

.action-buttons {
  display: flex;
  justify-content: center;
  margin-top: 30px;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
  font-weight: 600;
  font-size: 1em;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.back-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
}

.mobile-details-button-container {
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  padding: 16px 10px;
  border-top: 1px solid #e0e0e0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
  z-index: 100;
}

/* Mobile Details Button */
.mobile-details-button {
  width: 100%;
}

/* Bottom Sheet Styles */
.mobile-bottom-sheet {
  display: none;
}

.bottom-sheet-card {
  height: 80vh;
  border-radius: 20px 20px 0 0 !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.bottom-sheet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #fff;
  position: sticky;
  top: 0;
  z-index: 10;
}

.bottom-sheet-header h3 {
  margin: 0;
  font-size: 1.3rem;
  color: #333;
  font-weight: 600;
}

.bottom-sheet-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bottom-sheet-close:hover {
  background-color: #f5f5f5;
}

.mobile-sidebar-content {
  overflow-y: auto;
  flex: 1;
  padding-bottom: 20px;
}

@media (max-width: 1024px) {
  .instructional-content-wrapper {
    gap: 0;
  }

  .instructional-content {
    width: 100%;
    margin-right: 0;
  }

  .instructional-sidebar.desktop-only {
    display: none !important;
  }

  .mobile-details-button-container {
    display: flex;
  }

  .mobile-bottom-sheet {
    display: block;
  }
}

@media (max-width: 768px) {
  .instructional-preview-container {
    padding: 0px 15px 20px 15px;
  }

  .lesson-materials-forbidden-state {
    min-height: calc(100vh - 80px);
    padding: 8px 16px;
  }

  .lesson-materials-forbidden-content {
    gap: 16px;
  }

  .lesson-materials-forbidden-image {
    width: min(361px, 100%);
  }

  .lesson-materials-forbidden-copy h1 {
    font-size: var(--font-size-headline-medium);
    line-height: normal;
  }

  .lesson-materials-forbidden-copy p {
    font-size: var(--font-size-body-large);
    line-height: var(--line-height-body-large);
    letter-spacing: 0.08px;
  }

  .lesson-materials-forbidden-button {
    height: 40px;
    min-height: 40px;
    border-radius: 8px;
    padding: 10px 16px;
  }

  .lesson-materials-forbidden-button :deep(.action-button__text) {
    font-size: var(--font-size-title-small);
    line-height: var(--line-height-title-small);
    letter-spacing: 0.014px;
  }

  .lesson-materials-forbidden-button :deep(.v-icon) {
    width: 20px;
    height: 20px;
    font-size: 20px;
  }

  .instructional-header-content {
    flex-direction: column;
  }

  .instructional-main-container {
    flex-direction: column-reverse;
  }

  .instructional-content {
    width: 100%;
    margin-right: 0;
  }

  .instructional-tabs {
    overflow-x: auto;
    white-space: nowrap;
    padding-bottom: 10px;
  }

  .instructional-tab {
    display: inline-block;
  }

  .resources-buttons {
    width: 100%;
  }

  .resource-copy-button {
    font-size: var(--fs-medium);
    width: 100%;
    height: fit-content;
    padding: 10px 20px;
  }

  .instructional-title {
    font-size: var(--fs-xxl);
  }

  .instructional-meta {
    align-items: flex-start;
    font-size: 12px;
  }

  .instructional-meta-item {
    gap: 0px;
  }

  .sidebar-section {
    padding: 15px;
  }

  .material-item {
    padding: 10px;
  }

  .material-header {
    gap: 6px;
  }

  .included-badge {
    align-self: flex-start;
  }

  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .detail-value {
    text-align: left;
  }

  /* Standards section mobile adjustments */
  .standards-section {
    padding: 15px;
  }

  .standard-category {
    padding: 10px;
  }

  .standard-category-title {
    font-size: 0.9rem;
    margin-bottom: 6px;
  }

  .standard-terms {
    gap: 6px;
  }

  .standard-tag {
    font-size: 0.75rem;
    padding: 5px 10px;
  }

  .empty-standards-state {
    padding: 12px 8px;
  }

  .empty-standards-text {
    font-size: 0.7rem;
  }
}

/* Extra large screen */
@media (min-width: 2560px) {
  .instructional-preview-container {
    max-width: 2400px;
  }
}

.resource-views-banner {
  background-color: var(--white-hover-1);
  width: fit-content;
  margin: 40px auto;
  padding: 15px 20px;
  border-radius: 10px;
}

.resource-views-banner.mobile-only {
  display: none;
}

.banner-text {
  font-size: 1.2rem;
  color: #333;
  font-weight: 800;
}

.remaining-count {
  color: var(--primary-pink);
  font-weight: 800;
}

.login-signup-btn {
  background: none;
  border: none;
  color: var(--primary-pink);
  text-decoration: underline;
  cursor: pointer;
  font-size: 1.2rem;
  font-weight: 800;
  padding: 0;
  margin: 0 2px;
}

.login-signup-btn:hover {
  color: var(--primary-pink);
  text-decoration: none;
}

@media (max-width: 768px) {
  .slide-deck-content {
    flex-direction: column;
  }

  .instructional-header-content {
    gap: 0px;
  }

  .slide-deck-thumbnail {
    width: 100%;
  }

  .slide-deck-thumbnail img {
    height: auto;
    aspect-ratio: 469 / 265;
  }

  .slide-deck-info {
    border-left: none;
    padding-left: 0;
  }

  .slide-deck-notes {
    font-size: var(--fs-small);
    line-height: 20px;
    text-align: left;
  }

  .resource-views-banner {
    display: none;
  }

  .resource-views-banner.mobile-only {
    display: block;
    margin: 0px;
    font-size: var(--fs-medium);
  }

  .resource-views-banner.mobile-only .banner-text {
    font-size: var(--fs-medium);
  }

  .resource-views-banner.mobile-only .login-signup-btn {
    font-size: var(--fs-medium);
  }
}
</style>

<!-- Global styles for rendered content -->
<style>
/* Slide deck styles */
.slide-deck-container {
  margin: 1.5rem 0;
  border-radius: 8px;
  background-color: var(--white-hover-1);
}

.slide-deck-content {
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding: 8px;
  align-items: flex-start;
}

.slide-deck-thumbnail {
  flex-shrink: 0;
  width: 469px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.slide-deck-thumbnail:hover {
  opacity: 0.9;
}

.slide-deck-thumbnail img {
  width: 100%;
  height: 265px;
  object-fit: cover;
  border-radius: 0.75rem;
  transition: transform 0.2s ease;
}

.slide-deck-thumbnail:hover img {
  transform: scale(1.03);
}

.slide-deck-title {
  font-weight: 500;
  color: var(--black-body-text);
  margin-top: 0.5rem;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.08px;
}

.slide-deck-info {
  flex: 1 0 0;
  min-width: 1px;
  padding-left: 16px;
  border-left: 2px solid var(--neutral-100);
  align-self: center;
}

.slide-deck-notes {
  color: var(--black-body-text);
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.15px;
  font-family: "League Spartan", sans-serif;
  min-height: 50px;
}
</style>
